// src/cart/Components/CartModal.tsx
// Refactored to use extracted hooks and components
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import {
  FaTimes,
  FaTrash,
  FaPlus,
  FaMinus,
  FaWhatsapp,
  FaEdit,
  FaCog,
  FaCheck,
} from "react-icons/fa";
import { useBranch } from "~/contexts/BranchContext";
import type { MenuItem, DeliveryZone } from "~/types/new_types";
import ConfirmationModal from "~/components/ui/ConfirmationModal";
import { getBranchName } from "~/utils/brandHelper";
import { getItemName } from "~/utils/menuItemHelper";
import { insertOrder } from "~/utils/supabaseClient";
import type { Json } from "~/types/supabase";
import { useBackButtonInterceptor } from "~/utils/backwordButtonFunctionality";

// Import hooks
import { useCartItems } from "./hooks/useCartItems";
import { useCartCalculations } from "./hooks/useCartCalculations";
import { useLocation } from "./hooks/useLocation";

// Import components
import { OrderTypeSelector } from "./Components/OrderTypeSelector";
import { ContactForm } from "./Components/ContactForm";
import { DeliverySection } from "./Components/DeliverySection";
import { PickupSection } from "./Components/PickupSection";
import CrossSellingSection from "~/components/shared/CrossSelling/CrossSellingSection";

// Import types
import type { EnhancedCartItem, OrderType, CartTheme } from "./types";

interface CartModalProps {
  isOpen: boolean;
  onClose: () => void;
  onItemClick?: (item: MenuItem) => void;
}

const CartModal: React.FC<CartModalProps> = ({
  isOpen,
  onClose,
  onItemClick,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const {
    branch,
    branchData,
    theme,
    selectedItem,
    getConvertedPrice,
    selectedCurrency,
  } = useBranch();

  // Use extracted hooks
  const {
    enhancedCartItems,
    itemNotes,
    activeNoteInput,
    setActiveNoteInput,
    addNote,
    updateNote,
    deleteNote,
    expandedItems,
    toggleItemExpansion,
    expandedNotes,
    toggleNotesExpansion,
    updateQuantity,
    removeItem,
    clearAllItems,
    orderNote,
    setOrderNote,
  } = useCartItems();

  const { getItemTotalBreakdown, calculateTotal } = useCartCalculations();

  const {
    userLocation,
    locationError,
    isGettingLocation,
    getLocation,
  } = useLocation();

  // Local state for form fields
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [mobileNumber, setMobileNumber] = useState<string>("");
  const [phoneError, setPhoneError] = useState<string>("");
  const [isPhoneFocused, setIsPhoneFocused] = useState(false);
  const [detectedCountry, setDetectedCountry] = useState<string | undefined>(
    undefined
  );
  const [selectedCountry, setSelectedCountry] = useState<string | undefined>(
    undefined
  );
  const [manualAddress, setManualAddress] = useState<string>("");
  const [orderType, setOrderType] = useState<OrderType>("delivery");
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const [selectedZone, setSelectedZone] = useState<DeliveryZone | null>(null);
  const [selectedArrivalTime, setSelectedArrivalTime] = useState<string>("");
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});

  useBackButtonInterceptor(isOpen, onClose);

  // Initialize arrival time with default selection
  React.useEffect(() => {
    const arrivalConfig = (branchData?.branch as any)?.arrival_time;
    if (arrivalConfig?.is_enabled && arrivalConfig.time_options && !selectedArrivalTime) {
      const defaultIndex = arrivalConfig.default_selection || 0;
      const defaultTime = arrivalConfig.time_options[defaultIndex] || arrivalConfig.time_options[0];
      setSelectedArrivalTime(defaultTime);
    }
  }, [branchData, selectedArrivalTime]);

  // Theme values
  const cartTheme: CartTheme = useMemo(
    () => ({
      primaryColor: theme?.primaryColor || "#ea580c",
      secondaryColor: theme?.secondaryColor || "#f97316",
      backgroundColor: theme?.backgroundColor || "#ffffff",
      textColor: theme?.textColor || "#1f2937",
      borderColor: theme?.borderColor || "#d1d5db",
      titleFontFamily: theme?.titleFontFamily,
    }),
    [theme]
  );

  // Detect country from IP on component mount
  useEffect(() => {
    const detectCountry = async () => {
      try {
        const response = await fetch("https://ipapi.co/json/");
        const data = await response.json();
        if (data.country_code) {
          setDetectedCountry(data.country_code);
          setSelectedCountry(data.country_code);
        }
      } catch (error) {
      }
    };
    detectCountry();
  }, []);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  // Validate phone number
  const validatePhoneNumber = useCallback((phone: string): boolean => {
    if (!phone || phone.trim() === "") return false;
    try {
      const parsed = parsePhoneNumberFromString(phone);
      if (parsed) {
        return parsed.isValid();
      }
      const digitsOnly = phone.replace(/\D/g, "");
      return digitsOnly.length >= 8 && digitsOnly.length <= 15;
    } catch {
      return false;
    }
  }, []);

  // Validate form
  const validateForm = useCallback(() => {
    return (
      firstName.trim().length >= 2 &&
      lastName.trim().length >= 2 &&
      validatePhoneNumber(mobileNumber) &&
      (orderType === "pickup" ||
        (orderType === "delivery" && manualAddress.trim()))
    );
  }, [
    firstName,
    lastName,
    mobileNumber,
    orderType,
    manualAddress,
    validatePhoneNumber,
  ]);

  // Check if item is combo or meal
  const isComboOrMeal = useCallback((item: EnhancedCartItem): boolean => {
    const cardsHaveCombo = item.cards?.some((card) =>
      card.variants.some((variant) => {
        const hasComboType = variant.variantType === "meals_and_combos";
        const hasComboItems = variant.children.some((child) => {
          const comboItems = child.comboData?.items ?? [];
          return comboItems.length > 0;
        });
        return hasComboType || hasComboItems;
      })
    );

    const variantsHaveCombo = item.variants?.some((variant) => {
      const hasComboType = variant.variantType === "meals_and_combos";
      const hasComboItems = variant.children.some((child) => {
        const comboItems = child.comboData?.items ?? [];
        return comboItems.length > 0;
      });
      return hasComboType || hasComboItems;
    });

    return Boolean(cardsHaveCombo || variantsHaveCombo);
  }, []);

  // Build variant details for order
  const buildVariantDetails = useCallback(
    (
      item: EnhancedCartItem,
      note?: string
    ): Record<string, unknown> => {
      const details: Record<string, unknown> = {};

      if (item.cards && item.cards.length > 0) {
        details.cards = item.cards.map((card) => ({
          cardId: card.cardId,
          cardName: card.cardName,
          quantity: card.quantity,
          unitBasePrice:
            card.unitBasePrice !== undefined ? Number(card.unitBasePrice) : null,
          unitAddOnTotal:
            card.unitAddOnTotal !== undefined
              ? Number(card.unitAddOnTotal)
              : null,
          variants: card.variants.map((variant) => ({
            name: variant.name,
            isVariant: Boolean(variant.isVariant),
            variantType: variant.variantType ?? null,
            isAiSuggestion: Boolean(item.isAiSuggestion),
            actionType: variant.actionType ?? null,
            quantity: Number(variant.quantity ?? 0),
            children: variant.children.map((child) => ({
              name: child.name,
              price: Number(child.price ?? 0),
              imageUrl: child.imageUrl ?? null,
              comboData: child.comboData
                ? {
                  combo_price:
                    child.comboData.combo_price !== undefined &&
                      child.comboData.combo_price !== null
                      ? Number(child.comboData.combo_price)
                      : null,
                  items: (child.comboData.items ?? []).map((comboItem) => ({
                    item_name: comboItem.item_name ?? null,
                    quantity:
                      comboItem.quantity !== undefined &&
                        comboItem.quantity !== null
                        ? Number(comboItem.quantity)
                        : null,
                    note: comboItem.note ?? null,
                  })),
                }
                : null,
            })),
          })),
        }));
      } else if (item.variants && item.variants.length > 0) {
        details.variants = item.variants.map((variant) => ({
          name: variant.name,
          isVariant: Boolean(variant.isVariant),
          variantType: variant.variantType ?? null,
          actionType: variant.actionType ?? null,
          quantity: Number(variant.quantity ?? 0),
          isAiSuggestion: Boolean(item.isAiSuggestion),
          children: variant.children.map((child) => ({
            name: child.name,
            price: Number(child.price ?? 0),
            imageUrl: child.imageUrl ?? null,
            comboData: child.comboData
              ? {
                combo_price:
                  child.comboData.combo_price !== undefined &&
                    child.comboData.combo_price !== null
                    ? Number(child.comboData.combo_price)
                    : null,
                items: (child.comboData.items ?? []).map((comboItem) => ({
                  item_name: comboItem.item_name ?? null,
                  quantity:
                    comboItem.quantity !== undefined &&
                      comboItem.quantity !== null
                      ? Number(comboItem.quantity)
                      : null,
                  note: comboItem.note ?? null,
                })),
              }
              : null,
          })),
        }));
      }

      if (note && note.trim()) {
        details.item_note = note.trim();
      }

      return details;
    },
    []
  );

  // Build order details JSON
  const buildOrderDetailsJson = useCallback((): Json => {
    const branchId = branchData?.branch?.id || (branch as any)?.id || null;
    const currencyId = selectedCurrency?.id ?? null;

    const itemsDetail = enhancedCartItems.map((item) => {
      const breakdown = getItemTotalBreakdown(item, branchData?.items);
      const unitPrice = Number.isFinite(breakdown.unitPrice)
        ? Number(breakdown.unitPrice.toFixed(2))
        : 0;
      const notes = itemNotes[item.itemId] ?? [];
      const note = notes.length > 0 ? notes.join('; ') : undefined;
      const variantDetails = buildVariantDetails(item, note);
      const isAiSuggestion =
        item.cards?.some((card) => card.isAiSuggestion === true) ??
        Boolean(item.isAiSuggestion);

      return {
        item_id: item.itemId,
        item_price: unitPrice,
        quantity: item.totalQuantity,
        varient_details: Object.keys(variantDetails).length
          ? variantDetails
          : {},
        is_combo_or_meal: isComboOrMeal(item),
        is_ai_suggestion: isAiSuggestion,
      };
    });

    const payload: Record<string, unknown> = {
      branch_id: branchId,
      selected_currency_id: currencyId,
      total_price: Number(
        calculateTotal(enhancedCartItems, branchData?.items).toFixed(2)
      ),
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      phone_number: mobileNumber.trim(),
      items_detail: itemsDetail,
    };

    // Add delivery_zone_id only for delivery orders with a selected zone
    if (orderType === "delivery" && selectedZone?.id) {
      payload.delivery_zone_id = selectedZone.id;
    }

    // Add arrival_time for pickup orders
    if (orderType === "pickup" && selectedArrivalTime) {
      payload.expected_arrival_time = selectedArrivalTime;
    }

    return payload as Json;
  }, [
    branchData,
    branch,
    selectedCurrency,
    enhancedCartItems,
    firstName,
    lastName,
    mobileNumber,
    itemNotes,
    getItemTotalBreakdown,
    calculateTotal,
    buildVariantDetails,
    isComboOrMeal,
    orderType,
    selectedZone,
  ]);

  // Analytics helper
  const addOrderToAnalytics = useCallback(async (orderDetails: Json) => {
    try {
      const { data, error } = await insertOrder(orderDetails);
      if (error) {
      } else {
      }
    } catch (error) {
    }
  }, []);

  // Handle clear confirmation
  const handleConfirmClear = useCallback(() => {
    clearAllItems();
    setShowClearConfirmation(false);
    onClose();
  }, [clearAllItems, onClose]);

  // Get item image helper
  const getItemImage = useCallback(
    (item: EnhancedCartItem): string => {
      const menuItem = branchData?.items?.find((mi) => mi.id === item.itemId);
      return menuItem?.image_url || item.itemImage || "/placeholder-food.jpg";
    },
    [branchData?.items]
  );

  // Handle item click
  const handleItemClick = useCallback(
    (item: EnhancedCartItem) => {
      if (onItemClick) {
        const menuItem = branchData?.items?.find((mi) => mi.id === item.itemId);
        if (menuItem) {
          onItemClick(menuItem);
        }
      }
    },
    [onItemClick, branchData?.items]
  );

  // Format card variants for display
  const formatCardVariants = useCallback((card: any) => {
    if (!card?.variants || card.variants.length === 0) return null;

    const parts: string[] = [];
    card.variants.forEach((variant: any) => {
      if (variant.children?.length > 0) {
        variant.children.forEach((child: any) => {
          if (child.name) {
            parts.push(child.name);
          }
        });
      }
    });

    return parts.length > 0 ? parts.join(", ") : null;
  }, []);

  // Send to WhatsApp
  const sendToWhatsApp = useCallback(() => {
    if (!validateForm()) return;

    const total = calculateTotal(enhancedCartItems, branchData?.items);
    const trimmedOrderNote = orderNote.trim();
    const totalItems = enhancedCartItems.reduce((sum, item) => sum + item.totalQuantity, 0);

    // Build detailed order items with customizations
    let orderDetailsText = enhancedCartItems
      .map((item, index) => {
        const menuItem = branchData?.items?.find((mi) => mi.id === item.itemId);
        const itemDisplayName = menuItem
          ? getItemName(menuItem, i18n.language)
          : item.itemName;
        
        let itemText = ` ${index + 1}. ${itemDisplayName}\n Quantity: ${item.totalQuantity}`;
        
        // Add customizations if they exist
        if (item.cards && item.cards.length > 0) {
          const customizations: string[] = [];
          item.cards.forEach((card: any) => {
            const variantsText = formatCardVariants(card);
            if (variantsText) {
              customizations.push(`      • ${card.cardName}: ${variantsText}`);
            }
          });
          if (customizations.length > 0) {
            itemText += `\n Customizations:\n${customizations.join('\n')}`;
          }
        }
        
        // Add item notes (now stored as array)
        const notes = itemNotes[item.itemId] ?? [];
        if (notes.length > 0) {
          const notesText = notes.map((n, i) => `📝 Note ${i + 1}: ${n}`).join('\n  ');
          itemText += `\n  ${notesText}`;
        }
        
        // Add special instructions if any
        if (item.itemNotes && item.itemNotes.length > 0) {
          // Already handled above
        }
        
        return itemText;
      })
      .join('\n\n');

    const currentDate = new Date().toLocaleString();
    const orderTypeLabel = orderType === "delivery" ? "DELIVERY" : "PICKUP";
    
    // Calculate delivery fee if zone is selected
    const deliveryFee = orderType === "delivery" && selectedZone ? selectedZone.fee : 0;
    const grandTotal = total + deliveryFee;
    const zoneName = selectedZone 
      ? (i18n.language === "ar" ? selectedZone.ar_zone_name : selectedZone.zone_name)
      : "";

    let locationSection = "";
    if (orderType === "delivery") {
      locationSection = `═══ DELIVERY INFORMATION ═══
Customer Name: ${firstName} ${lastName}
Phone: ${mobileNumber}
Address: ${manualAddress}${userLocation ? `\nDetected Location: ${userLocation}` : ""}${selectedZone ? `\nDelivery Zone: ${zoneName}\nDelivery Fee: ${getConvertedPrice(deliveryFee)}` : ""}`;
    } else {
      locationSection = `═══ PICKUP INFORMATION ═══
Customer Name: ${firstName} ${lastName}
Phone: ${mobileNumber}${selectedArrivalTime ? `\nExpected Arrival Time: ${(() => {
        const [hours, minutes] = selectedArrivalTime.split(':');
        const totalMinutes = parseInt(hours) * 60 + parseInt(minutes);
        if (totalMinutes < 60) return `${totalMinutes} minutes`;
        const hrs = Math.floor(totalMinutes / 60);
        const mins = totalMinutes % 60;
        return mins === 0 ? `${hrs} hour${hrs > 1 ? 's' : ''}` : `${hrs} hour${hrs > 1 ? 's' : ''} ${mins} minutes`;
      })()}` : ""}`;
    }

    const message = `NEW ORDER - ${branch?.name || "DINE.AI"}

Date: ${currentDate}
Order Type: ${orderTypeLabel}

═══ ORDER SUMMARY ═══
Total Items: ${totalItems}
Subtotal: ${getConvertedPrice(total)}${deliveryFee > 0 ? `
Delivery Fee: ${getConvertedPrice(deliveryFee)}` : ""}
Grand Total: ${getConvertedPrice(grandTotal)}

═══ ORDER DETAILS ═══
${orderDetailsText}

${locationSection}
${trimmedOrderNote ? `
═══ CUSTOMER NOTE ═══
${trimmedOrderNote}
` : ""}
Thank you for choosing ${branch?.name || "DINE.AI"}!`;

    const whatsappNumber = branch?.phone?.replace(/[^\d]/g, "") || "";
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
      message
    )}`;

    const orderJson = buildOrderDetailsJson();
    void addOrderToAnalytics(orderJson);

    window.open(whatsappUrl, "_blank");
  }, [
    validateForm,
    calculateTotal,
    enhancedCartItems,
    branchData,
    orderNote,
    itemNotes,
    formatCardVariants,
    i18n.language,
    orderType,
    manualAddress,
    userLocation,
    getConvertedPrice,
    firstName,
    lastName,
    mobileNumber,
    branch,
    buildOrderDetailsJson,
    addOrderToAnalytics,
    selectedZone,
  ]);

  const getNoteEditKey = useCallback((itemId: string, noteIndex: number) => {
    return `${itemId}:${noteIndex}`;
  }, []);

  const startEditingNote = useCallback(
    (itemId: string, noteIndex: number, note: string) => {
      const noteKey = getNoteEditKey(itemId, noteIndex);
      setActiveNoteInput(noteKey);
      setNoteDrafts((prev) => ({
        ...prev,
        [noteKey]: note,
      }));
    },
    [getNoteEditKey, setActiveNoteInput]
  );

  const cancelEditingNote = useCallback(
    (itemId: string, noteIndex: number) => {
      const noteKey = getNoteEditKey(itemId, noteIndex);
      setActiveNoteInput(null);
      setNoteDrafts((prev) => {
        const next = { ...prev };
        delete next[noteKey];
        return next;
      });
    },
    [getNoteEditKey, setActiveNoteInput]
  );

  const submitEditingNote = useCallback(
    (itemId: string, noteIndex: number) => {
      const noteKey = getNoteEditKey(itemId, noteIndex);
      const draft = noteDrafts[noteKey] ?? "";
      updateNote(itemId, noteIndex, draft);
      setNoteDrafts((prev) => {
        const next = { ...prev };
        delete next[noteKey];
        return next;
      });
    },
    [getNoteEditKey, noteDrafts, updateNote]
  );

  if (!isOpen) return null;

  const modalZIndex = selectedItem ? "z-45" : "z-50";

  const handleOverlayPointerDown = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      <ConfirmationModal
        key="cart-confirmation-modal"
        isOpen={showClearConfirmation}
        title={t("menu.cart.confirmClearCartTitle")}
        message={t("menu.cart.confirmClearCartMessage")}
        confirmText={t("menu.cart.clearAll")}
        cancelText={t("menu.cart.cancel")}
        onConfirm={handleConfirmClear}
        onCancel={() => setShowClearConfirmation(false)}
        theme={theme || undefined}
        isDangerous={true}
      />

      <style key="cart-scrollbar-style">
        {`
          .cart-scroll-container::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>

      <motion.div
        key="cart-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={`fixed inset-0 backdrop-blur-sm ${modalZIndex} flex items-center justify-center p-4`}
        onPointerDown={handleOverlayPointerDown}
        style={{ backdropFilter: "blur(10px)" }}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="bg-white rounded-2xl shadow-2xl h-full w-full max-w-md max-h-[90vh] overflow-hidden relative flex flex-col"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          style={{
            border: `2px solid ${cartTheme.primaryColor}`,
            backgroundColor: cartTheme.backgroundColor,
          }}
        >
          {/* Header */}
          <div className="text-white relative min-h-[56px] flex items-center justify-center px-4">
            <button
              onClick={onClose}
              className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? "left-4" : "right-4"
                } w-8 h-8 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer z-10`}
              style={{ backgroundColor: cartTheme.primaryColor }}
            >
              <FaTimes size={14} />
            </button>

            {enhancedCartItems.length > 0 && (
              <button
                onClick={() => setShowClearConfirmation(true)}
                className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? "right-4" : "left-4"
                  } w-8 h-8 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors text-white shadow-lg cursor-pointer z-10`}
              >
                <FaTrash size={14} />
              </button>
            )}

            <div className="w-full max-w-[calc(100%-96px)] mx-auto text-center px-1">
              <span
                className="block text-2xl leading-tight break-words"
                style={{
                  color: cartTheme.textColor,
                  textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                  fontFamily: cartTheme.titleFontFamily || "inherit",
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {branch ? getBranchName(branch, i18n.language) : ""}
              </span>
            </div>
          </div>

          {/* Content */}
          <div
            className="flex-1 overflow-y-auto min-h-0 cart-scroll-container"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {enhancedCartItems.length === 0 ? (
              <div className="p-8 text-center">
                <div
                  className="text-4xl mb-4"
                  style={{ color: cartTheme.textColor }}
                >
                  🛒
                </div>
                <p style={{ color: cartTheme.textColor }}>
                  {t("menu.cart.empty")}
                </p>
              </div>
            ) : (
              <div className="p-3">
                {/* Cart Items */}
                {enhancedCartItems.map((item, itemIndex) => {
                  const breakdown = getItemTotalBreakdown(
                    item,
                    branchData?.items
                  );
                  const isExpanded = expandedItems.has(item.itemId);

                  return (
                    <motion.div
                      key={`${item.itemId}-${itemIndex}`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="bg-white rounded-lg p-3 shadow-sm transition-shadow duration-200 mb-2"
                      style={{ backgroundColor: cartTheme.backgroundColor }}
                    >
                      <div
                        className="cursor-pointer rounded-lg transition-colors duration-200"
                        onClick={() => handleItemClick(item)}
                      >
                        <div className="flex items-start gap-2">
                          <div className="relative">
                            <img
                              src={getItemImage(item)}
                              alt={item.itemName}
                              className="w-16 h-16 rounded-lg object-cover shadow-md"
                            />
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between mb-1">
                              <h3
                                className="font-semibold text-gray-800 text-sm leading-tight"
                                style={{ color: cartTheme.textColor }}
                              >
                                {(() => {
                                  const menuItem = branchData?.items?.find(
                                    (mi) => mi.id === item.itemId
                                  );
                                  return menuItem
                                    ? getItemName(menuItem, i18n.language)
                                    : item.itemName;
                                })()}
                              </h3>
                            </div>

                            <div>
                              {breakdown.variantsTotal > 0 && (
                                <div className="flex items-center text-xs">
                                  <span style={{ color: cartTheme.textColor }}>
                                    {t("menuItem.addons")}
                                  </span>
                                  <span
                                    className="font-medium"
                                    style={{ color: cartTheme.secondaryColor }}
                                  >
                                    +
                                    {getConvertedPrice(breakdown.variantsTotal)}
                                  </span>
                                </div>
                              )}

                              <p
                                className="font-bold text-base"
                                style={{ color: cartTheme.textColor }}
                              >
                                {getConvertedPrice(breakdown.grandTotal)}
                              </p>
                            </div>

                            {item.cards && item.cards.length > 0 && (
                              <div className="mt-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleItemExpansion(item.itemId);
                                  }}
                                  className="flex items-center gap-2 text-xs px-2 py-1 rounded transition-colors cursor-pointer"
                                  style={{ color: cartTheme.textColor }}
                                >
                                  <span>
                                    {isExpanded
                                      ? t("menuItem.hide")
                                      : t("menuItem.show")}{" "}
                                    {item.cards.length} {t("menu.item")}
                                  </span>
                                  <motion.div
                                    animate={{ rotate: isExpanded ? 180 : 0 }}
                                    transition={{ duration: 0.2 }}
                                  >
                                    ▼
                                  </motion.div>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Expanded cards */}
                        <AnimatePresence>
                          {isExpanded &&
                            item.cards &&
                            item.cards.length > 0 && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className="space-y-2 pt-3"
                              >
                                {item.cards.map((card, cardIndex) => {
                                  const cardVariants = formatCardVariants(card);
                                  return (
                                    <div
                                      key={
                                        card.cardId ||
                                        `${item.itemId}-card-${cardIndex}`
                                      }
                                      className="p-2 rounded"
                                    >
                                      <div className="flex items-center justify-between mb-2">
                                        <h4
                                          className="font-medium text-sm"
                                          style={{ color: cartTheme.textColor }}
                                        >
                                          {card.cardName}
                                        </h4>
                                        <span
                                          className="text-xs px-2 rounded-full"
                                          style={{
                                            backgroundColor: `${cartTheme.primaryColor}20`,
                                            color: cartTheme.primaryColor,
                                          }}
                                        >
                                          {t("menu.quantity")} {card.quantity}
                                        </span>
                                      </div>
                                        {cardVariants && (
                                        <div
                                          className="text-xs rounded"
                                          style={{
                                            color: cartTheme.textColor,
                                            backgroundColor:
                                              cartTheme.backgroundColor,
                                          }}
                                        >
                                          <div className="flex items-start gap-2">
                                            <FaCog
                                              size={10}
                                              className="mt-0.5"
                                              style={{
                                                color: cartTheme.secondaryColor,
                                              }}
                                            />
                                            <span>{cardVariants}</span>
                                          </div>
                                        </div>
                                      )}
                                      {/* Notes are now at item level, not per-card */}
                                    </div>
                                  );
                                })}
                              </motion.div>
                            )}
                        </AnimatePresence>
                      </div>

                      {/* Quantity controls and note button */}
                      <div className="flex items-center justify-between pt-2">
                        {(() => {
                          // Check if item has any notes
                          const notes = itemNotes[item.itemId] ?? [];
                          const hasAnyNote = notes.length > 0;
                          return (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleNotesExpansion(item.itemId);
                              }}
                              className="text-xs hover:opacity-90 flex items-center gap-1 px-2 py-1 rounded transition-colors cursor-pointer"
                              style={{ 
                                color: hasAnyNote ? cartTheme.primaryColor : cartTheme.textColor,
                                backgroundColor: hasAnyNote ? `${cartTheme.primaryColor}15` : "transparent"
                              }}
                            >
                              <FaEdit size={10} />
                              {hasAnyNote
                                ? `${t("menu.cart.editNote")} (${notes.length})`
                                : t("menu.cart.addNote")}
                            </button>
                          );
                        })()}

                        <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (item.totalQuantity === 1) {
                                removeItem(item.itemId);
                              } else {
                                updateQuantity(
                                  item.itemId,
                                  item.totalQuantity - 1
                                );
                              }
                            }}
                            className="w-7 h-7 rounded-full flex items-center justify-center hover:opacity-80 transition-colors shadow-md cursor-pointer"
                            style={
                              item.totalQuantity === 1
                                ? {
                                  backgroundColor: cartTheme.primaryColor,
                                  color: "white",
                                }
                                : {
                                  backgroundColor: `${cartTheme.primaryColor}20`,
                                  color: cartTheme.primaryColor,
                                }
                            }
                          >
                            {item.totalQuantity === 1 ? (
                              <FaTrash size={10} />
                            ) : (
                              <FaMinus size={10} />
                            )}
                          </button>
                          <span
                            className="font-bold text-base min-w-[24px] text-center"
                            style={{ color: cartTheme.textColor }}
                          >
                            {item.totalQuantity}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              updateQuantity(
                                item.itemId,
                                item.totalQuantity + 1
                              );
                            }}
                            className="w-7 h-7 rounded-full flex items-center justify-center hover:opacity-80 transition-colors shadow-md cursor-pointer"
                            style={{
                              backgroundColor: cartTheme.primaryColor,
                              color: "white",
                            }}
                          >
                            <FaPlus size={10} />
                          </button>
                        </div>
                      </div>

                      {/* Notes section - collapse/expand with add/edit/delete */}
                      <AnimatePresence>
                        {expandedNotes.has(item.itemId) && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="mt-2 w-full"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex flex-col gap-2">
                              {/* Existing notes list with edit/delete */}
                              {(itemNotes[item.itemId] ?? []).map((note, idx) => (
                                (() => {
                                  const noteKey = getNoteEditKey(item.itemId, idx);
                                  const isEditing = activeNoteInput === noteKey;

                                  return (
                                    <div
                                      key={idx}
                                      className="flex items-start gap-2 text-xs p-2 rounded"
                                      style={{
                                        backgroundColor: `${cartTheme.primaryColor}10`,
                                      }}
                                    >
                                      {isEditing ? (
                                        <input
                                          type="text"
                                          value={noteDrafts[noteKey] ?? note}
                                          autoFocus
                                          className="flex-1 text-xs px-2 py-1.5 rounded border outline-none"
                                          style={{
                                            borderColor: cartTheme.primaryColor,
                                            color: cartTheme.textColor,
                                            backgroundColor: cartTheme.backgroundColor,
                                          }}
                                          onChange={(e) => {
                                            const nextValue = e.currentTarget.value;
                                            setNoteDrafts((prev) => ({
                                              ...prev,
                                              [noteKey]: nextValue,
                                            }));
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                              e.preventDefault();
                                              submitEditingNote(item.itemId, idx);
                                            }
                                            if (e.key === "Escape") {
                                              e.preventDefault();
                                              cancelEditingNote(item.itemId, idx);
                                            }
                                          }}
                                        />
                                      ) : (
                                        <span className="flex-1 italic" style={{ color: cartTheme.textColor }}>
                                          📝 {note}
                                        </span>
                                      )}
                                      <div className="flex gap-1">
                                        <button
                                          onClick={() => {
                                            if (isEditing) {
                                              submitEditingNote(item.itemId, idx);
                                              return;
                                            }
                                            startEditingNote(item.itemId, idx, note);
                                          }}
                                          className="p-1 rounded hover:opacity-80 cursor-pointer"
                                          style={{ color: cartTheme.primaryColor }}
                                          title={isEditing ? t("menu.saveToOrder") : t("menu.cart.editNote")}
                                        >
                                          {isEditing ? <FaCheck size={10} /> : <FaEdit size={10} />}
                                        </button>
                                        <button
                                          onClick={() => {
                                            if (isEditing) {
                                              cancelEditingNote(item.itemId, idx);
                                              return;
                                            }
                                            deleteNote(item.itemId, idx);
                                          }}
                                          className="p-1 rounded hover:opacity-80 cursor-pointer"
                                          style={{ color: cartTheme.primaryColor }}
                                          title={isEditing ? t("menu.cart.cancel") : t("menu.cart.delete")}
                                        >
                                          <FaTrash size={10} />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })()
                              ))}
                              
                              {/* Add new note input */}
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  placeholder={t("menu.cart.notePlaceholder")}
                                  className="flex-1 text-sm px-3 py-2 rounded-lg border outline-none"
                                  style={{
                                    borderColor: cartTheme.primaryColor,
                                    color: cartTheme.textColor,
                                    backgroundColor: cartTheme.backgroundColor,
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" && e.currentTarget.value.trim()) {
                                      addNote(item.itemId, e.currentTarget.value);
                                      e.currentTarget.value = "";
                                    }
                                  }}
                                  id={`note-input-${item.itemId}`}
                                />
                                <button
                                  onClick={() => {
                                    const input = document.getElementById(`note-input-${item.itemId}`) as HTMLInputElement;
                                    if (input && input.value.trim()) {
                                      addNote(item.itemId, input.value);
                                      input.value = "";
                                    }
                                  }}
                                  className="px-3 py-1.5 text-xs rounded-lg flex items-center gap-1 cursor-pointer"
                                  style={{
                                    backgroundColor: cartTheme.primaryColor,
                                    color: "white",
                                  }}
                                >
                                  <FaPlus size={10} />
                                  {t("menu.cart.addNote")}
                                </button>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}

                {/* Cross-Selling Section */}
                {(() => {
                  // Debug: log enhancedCartItems and branchData.items
                
                  // Aggregate suggestions from all cart items
                  const allSuggestions = enhancedCartItems.flatMap((item) => {
                    const menuItem = branchData?.items?.find((mi) => mi.id === item.itemId);
                    return menuItem?.suggestions || [];
                  });
                  
                  // Deduplicate by item_id
                  const uniqueSuggestions = allSuggestions.filter(
                    (suggestion, index, self) =>
                      index === self.findIndex((s) => s.item_id === suggestion.item_id)
                  );
                   
                  return uniqueSuggestions.length > 0 ? (
                    <CrossSellingSection suggestions={uniqueSuggestions} />
                  ) : null;
                })()}

                {/* Order Type Selector */}
                <OrderTypeSelector
                  orderType={orderType}
                  setOrderType={setOrderType}
                  theme={cartTheme}
                />

                {/* Contact Form */}
                <ContactForm
                  firstName={firstName}
                  setFirstName={setFirstName}
                  lastName={lastName}
                  setLastName={setLastName}
                  mobileNumber={mobileNumber}
                  setMobileNumber={setMobileNumber}
                  orderNote={orderNote}
                  setOrderNote={setOrderNote}
                  phoneError={phoneError}
                  setPhoneError={setPhoneError}
                  isPhoneFocused={isPhoneFocused}
                  setIsPhoneFocused={setIsPhoneFocused}
                  detectedCountry={detectedCountry}
                  selectedCountry={selectedCountry}
                  setSelectedCountry={setSelectedCountry}
                  validatePhoneNumber={validatePhoneNumber}
                  theme={cartTheme}
                />

                {/* Delivery/Pickup Section */}
                {orderType === "delivery" ? (
                  <DeliverySection
                    manualAddress={manualAddress}
                    setManualAddress={setManualAddress}
                    userLocation={userLocation}
                    locationError={locationError}
                    isGettingLocation={isGettingLocation}
                    getLocation={getLocation}
                    branch={branch}
                    theme={cartTheme}
                    deliveryZones={branchData?.delivery_zones}
                    selectedZone={selectedZone}
                    setSelectedZone={setSelectedZone}
                    formatPrice={getConvertedPrice}
                  />
                ) : (
                  <PickupSection
                    userLocation={userLocation}
                    isGettingLocation={isGettingLocation}
                    getLocation={getLocation}
                    branch={branch}
                    theme={cartTheme}
                    arrivalTimeConfig={(branchData?.branch as any)?.arrival_time}
                    selectedArrivalTime={selectedArrivalTime}
                    onArrivalTimeChange={setSelectedArrivalTime}
                  />
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          {enhancedCartItems.length > 0 && (() => {
            const subtotal = calculateTotal(enhancedCartItems, branchData?.items);
            const deliveryFee = orderType === "delivery" && selectedZone ? selectedZone.fee : 0;
            const grandTotal = subtotal + deliveryFee;
            
            return (
              <div
                className="p-4"
                style={{ backgroundColor: `${cartTheme.primaryColor}08` }}
              >
                {/* Show breakdown when there's a delivery fee */}
                {deliveryFee > 0 && (
                  <div className="mb-3 px-2 space-y-1">
                    <div 
                      className="flex justify-between items-center text-sm" 
                      style={{ color: cartTheme.textColor }}
                    >
                      <span>{t("menu.cart.subtotal")}</span>
                      <span>{getConvertedPrice(subtotal)}</span>
                    </div>
                    <div 
                      className="flex justify-between items-center text-sm" 
                      style={{ color: cartTheme.primaryColor }}
                    >
                      <span>{t("menu.cart.deliveryFee")}</span>
                      <span>{getConvertedPrice(deliveryFee)}</span>
                    </div>
                    <div 
                      className="border-t pt-2 mt-2" 
                      style={{ borderColor: `${cartTheme.borderColor}50` }} 
                    />
                  </div>
                )}
                
                <div className="flex justify-between items-center mb-3 px-2">
                  <span
                    className="text-lg font-semibold"
                    style={{ color: cartTheme.primaryColor }}
                  >
                    {t("menu.cart.total")}
                  </span>
                  <span
                    className="text-2xl font-bold"
                    style={{ color: cartTheme.primaryColor }}
                  >
                    {getConvertedPrice(grandTotal)}
                  </span>
                </div>

                <button
                  onClick={sendToWhatsApp}
                  disabled={!validateForm()}
                  className="w-full py-3 px-4 rounded-xl font-semibold flex items-center justify-center gap-3 transition-all shadow-lg hover:shadow-xl disabled:shadow-none text-base"
                  style={{
                    backgroundColor: validateForm() ? "#25D366" : "#d1d5db",
                    color: validateForm() ? "white" : "#6b7280",
                    cursor: validateForm() ? "pointer" : "not-allowed",
                    opacity: validateForm() ? 1 : 0.6,
                  }}
                >
                  <FaWhatsapp size={20} />
                  {t("menu.cart.orderViaWhatsApp")}
                </button>
              </div>
            );
          })()}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CartModal;
